import React, { useReducer, createContext, useContext } from 'react';
import { FarmDeliveries } from 'types/FarmData';
import { ProductData } from 'types/ProductData';
import { validateProduct } from 'utils/productValidationHelper';
import {
  calculateFarmPriceTotals,
  calculateTotalPrice,
  PriceTotalProps,
} from 'utils/productTotalPriceHelper';
import { CouponDiscounts } from '../../interfaces/Coupon';

export interface ParcelLocation {
  id?: number;
  name?: string;
  address?: string;
}

export interface SelectedDelivery {
  id?: number;
  city?: string;
  address?: string;
  firm_name?: string;
  name?: string;
  selectedDeliveryId?: number;
  price: number | null;
}

export interface CartProduct {
  cartProduct?: ProductData;
  amount: number;
  vat?: number;
  stock_total?: number;
  requires_auth?: boolean;
}

export interface FarmProduct {
  farm_id: number;
  farm_name?: string;
  farmPriceTotals: PriceTotalProps;
  products: CartProduct[];
  farmDeliveries: FarmDeliveries[] | undefined;
  selectedDelivery?: SelectedDelivery;
}

interface CartState {
  farmProducts: FarmProduct[];
  priceTotal: number;
  coupons: CouponDiscounts[];
  requiresAuth: boolean;
}

export enum CartActionType {
  ADD_ITEM = 'ADD_ITEM',
  REMOVE_ITEM = 'REMOVE_ITEM',
  CLEAR_CART = 'CLEAR_CART',
  REFETCH_CART_PRODUCTS = 'REFETCH_CART_PRODUCTS',
  SET_DELIVERY = 'SET_DELIVERY',
  REMOVE_ALL_SELECTED_DELIVERIES = 'REMOVE_ALL_SELECTED_DELIVERIES',
  ADD_COUPON = 'ADD_COUPON',
  REMOVE_COUPON = 'REMOVE_COUPON',
  AUTHORIZE_CART = 'AUTHORIZE_CART',
}

type CartAction =
  | {
      type: CartActionType.ADD_ITEM;
      product: ProductData | undefined;
      amount: number;
    }
  | {
      type: CartActionType.REMOVE_ITEM;
      farmId: number;
      productId: number;
    }
  | { type: CartActionType.CLEAR_CART }
  | {
      type: CartActionType.REFETCH_CART_PRODUCTS;
      farmProducts: FarmProduct[];
      priceTotal: number;
      requiresAuth: boolean;
    }
  | {
      type: CartActionType.SET_DELIVERY;
      farmId: number;
      delivery: SelectedDelivery;
    }
  | { type: CartActionType.REMOVE_ALL_SELECTED_DELIVERIES }
  | { type: CartActionType.ADD_COUPON; coupon: CouponDiscounts }
  | { type: CartActionType.REMOVE_COUPON; code: string }
  | { type: CartActionType.AUTHORIZE_CART };

const CartStateContext = createContext<CartState | undefined>(undefined);
const CartDispatchContext = createContext<
  React.Dispatch<CartAction> | undefined
>(undefined);

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case CartActionType.ADD_ITEM: {
      const { product: newProduct, amount } = action;
      let requiresAuth = false;
      if (!newProduct) return state;

      const updatedFarmProducts: FarmProduct[] = [...state.farmProducts];

      let farmProduct = updatedFarmProducts.find(
        (farmProduct) => farmProduct.farm_id === newProduct.farm_id
      );

      if (farmProduct) {
        let cartProduct = farmProduct.products.find(
          (cartProduct) => cartProduct.cartProduct?.id === newProduct.id
        );

        if (cartProduct) {
          const validatedProduct = validateProduct({
            product: newProduct,
            count: Number((cartProduct.amount + amount).toFixed(3)),
          });

          cartProduct.stock_total = validatedProduct.stock_total;
          cartProduct.amount = validatedProduct.stock_count;
          cartProduct.requires_auth = validatedProduct.requires_auth;

          if (validatedProduct.requires_auth) {
            requiresAuth = true;
          }
        } else {
          const validatedProduct = validateProduct({
            product: newProduct,
            count: amount,
          });

          const newCartProduct = {
            cartProduct: newProduct,
            amount: validatedProduct.stock_count,
            stock_total: validatedProduct.stock_total,
            requiresAuth: validatedProduct.requires_auth,
          };

          farmProduct.products.push(newCartProduct);
          if (validatedProduct.requires_auth) {
            state.requiresAuth = true;
          }
        }

        const farmPriceTotals = calculateFarmPriceTotals(
          farmProduct.products,
          state.coupons
        );

        farmProduct.farmPriceTotals = farmPriceTotals;
      } else {
        const validatedProduct = validateProduct({
          product: newProduct,
          count: amount,
        });

        const products: CartProduct[] = [
          {
            cartProduct: newProduct,
            amount: validatedProduct.stock_count,
            stock_total: validatedProduct.stock_total,
            requires_auth: validatedProduct.requires_auth,
          },
        ];

        const farmPriceTotals = calculateFarmPriceTotals(
          products,
          state.coupons
        );

        const newFarmProduct: FarmProduct = {
          farm_id: newProduct.farm_id,
          farm_name: newProduct.farm_name,
          farmDeliveries: newProduct.farm?.deliveries,
          products,
          farmPriceTotals,
        };

        updatedFarmProducts.push(newFarmProduct);

        if (validatedProduct.requires_auth) {
          requiresAuth = true;
        }
      }

      const newPriceTotal = calculateTotalPrice(updatedFarmProducts);

      localStorage.setItem('farmProducts', JSON.stringify(updatedFarmProducts));
      localStorage.setItem('priceTotal', JSON.stringify(newPriceTotal));

      state.requiresAuth = requiresAuth;

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
      };
    }

    case CartActionType.REMOVE_ITEM: {
      const { farmId, productId } = action;
      const updatedFarmProducts = state.farmProducts.reduce<FarmProduct[]>(
        (acc, farm) => {
          if (farm.farm_id === farmId) {
            const updatedProducts = farm.products.filter(
              (cartProduct) => cartProduct.cartProduct?.id !== productId
            );

            if (!updatedProducts.length) {
              return acc;
            }

            const farmPriceTotals = calculateFarmPriceTotals(
              updatedProducts,
              state.coupons
            );

            acc.push({
              ...farm,
              products: updatedProducts,
              farmPriceTotals,
            });
          } else {
            acc.push(farm);
          }

          return acc;
        },
        []
      );

      // todo: test this
      const remainingProductIds = updatedFarmProducts
        .flatMap((farm) => farm.products.map((p) => p.cartProduct?.id))
        .filter((id): id is number => !!id);
      const updatedCoupons = state.coupons.filter((c) =>
        Object.keys(c.productDiscounts ?? {}).some((pid) =>
          remainingProductIds.includes(Number(pid))
        )
      );
      const recalculatedFarmProducts = updatedFarmProducts.map((farm) => ({
        ...farm,
        farmPriceTotals: calculateFarmPriceTotals(
          farm.products,
          updatedCoupons
        ),
      }));

      const newPriceTotal = calculateTotalPrice(recalculatedFarmProducts);

      localStorage.setItem(
        'farmProducts',
        JSON.stringify(recalculatedFarmProducts)
      );
      localStorage.setItem('priceTotal', JSON.stringify(newPriceTotal));
      localStorage.setItem('coupons', JSON.stringify(updatedCoupons));

      state.requiresAuth = updatedFarmProducts.some((f) =>
        f.products.some((p) => p.requires_auth)
      );

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
        coupons: updatedCoupons,
      };
    }

    case CartActionType.CLEAR_CART: {
      localStorage.removeItem('farmProducts');
      localStorage.removeItem('priceTotal');
      localStorage.removeItem('coupons');

      return {
        farmProducts: [],
        priceTotal: 0,
        coupons: [],
        requiresAuth: false,
      };
    }

    case CartActionType.REFETCH_CART_PRODUCTS: {
      const { farmProducts, priceTotal, requiresAuth } = action;

      localStorage.setItem('farmProducts', JSON.stringify(farmProducts));
      localStorage.setItem('priceTotal', JSON.stringify(priceTotal));

      state.requiresAuth = requiresAuth;

      return {
        ...state,
        farmProducts: farmProducts,
        priceTotal: priceTotal,
      };
    }

    case CartActionType.SET_DELIVERY: {
      const { farmId, delivery } = action;

      const updatedFarmProducts = state.farmProducts.map((farmProduct) => {
        if (farmProduct.farm_id === farmId) {
          return {
            ...farmProduct,
            selectedDelivery: delivery,
          };
        }
        return farmProduct;
      });

      const newPriceTotal = calculateTotalPrice(updatedFarmProducts);

      localStorage.setItem('farmProducts', JSON.stringify(updatedFarmProducts));
      localStorage.setItem('priceTotal', JSON.stringify(newPriceTotal));

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
      };
    }

    case CartActionType.REMOVE_ALL_SELECTED_DELIVERIES: {
      const updatedFarmProducts = state.farmProducts.map((farmProduct) => {
        return {
          ...farmProduct,
          selectedDelivery: undefined,
        };
      });

      const newPriceTotal = calculateTotalPrice(updatedFarmProducts);

      localStorage.setItem('farmProducts', JSON.stringify(updatedFarmProducts));
      localStorage.setItem('priceTotal', JSON.stringify(newPriceTotal));

      return {
        ...state,
        farmProducts: updatedFarmProducts,
        priceTotal: newPriceTotal,
      };
    }

    case CartActionType.ADD_COUPON: {
      const addedCoupons = [...state.coupons, action.coupon];
      const updatedFarmProducts = state.farmProducts.map((farm) => ({
        ...farm,
        farmPriceTotals: calculateFarmPriceTotals(farm.products, addedCoupons),
      }));
      const newTotal = calculateTotalPrice(updatedFarmProducts);
      localStorage.setItem('coupons', JSON.stringify(addedCoupons));
      localStorage.setItem('farmProducts', JSON.stringify(updatedFarmProducts));
      localStorage.setItem('priceTotal', JSON.stringify(newTotal));
      return {
        ...state,
        coupons: addedCoupons,
        farmProducts: updatedFarmProducts,
        priceTotal: newTotal,
      };
    }

    case CartActionType.REMOVE_COUPON: {
      const remainingCoupons = state.coupons.filter(
        (c) => c.code !== action.code
      );
      const updatedFarmProducts = state.farmProducts.map((farm) => ({
        ...farm,
        farmPriceTotals: calculateFarmPriceTotals(
          farm.products,
          remainingCoupons
        ),
      }));
      const newTotal = calculateTotalPrice(updatedFarmProducts);
      localStorage.setItem('coupons', JSON.stringify(remainingCoupons));
      localStorage.setItem('farmProducts', JSON.stringify(updatedFarmProducts));
      localStorage.setItem('priceTotal', JSON.stringify(newTotal));
      return {
        ...state,
        coupons: remainingCoupons,
        farmProducts: updatedFarmProducts,
        priceTotal: newTotal,
      };
    }

    case CartActionType.AUTHORIZE_CART:
      state.requiresAuth = false;

      return { ...state };
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const initialState: CartState = {
    farmProducts: JSON.parse(localStorage.getItem('farmProducts') || '[]'),
    priceTotal: JSON.parse(localStorage.getItem('priceTotal') || '0'),
    coupons: JSON.parse(localStorage.getItem('coupons') || '[]'),
    requiresAuth: false,
  };

  const [state, dispatch] = useReducer(cartReducer, initialState);

  return (
    <CartStateContext.Provider value={state}>
      <CartDispatchContext.Provider value={dispatch}>
        {children}
      </CartDispatchContext.Provider>
    </CartStateContext.Provider>
  );
}

export function useCartState() {
  const context = useContext(CartStateContext);
  if (context === undefined) {
    throw new Error('useCartState must be used within a CartProvider');
  }
  return context;
}

export function useCartDispatch() {
  const context = useContext(CartDispatchContext);
  if (context === undefined) {
    throw new Error('useCartDispatch must be used within a CartProvider');
  }
  return context;
}
